import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { initSession, isSignedIn, usingDevAuth } from './lib/session';

/**
 * design.md §13.1 — "DIRECTIVE — table state lives in the URL. Sort, filters,
 * page and column visibility are query parameters, so a view can be sent to a
 * colleague and survives a refresh."
 *
 * Which is why the catalog route declares no state of its own: everything the
 * table needs is read from and written to `route.query`.
 *
 * The catalog surfaces exist, and P2's do now as well: orders, customers and
 * the review queue. §1.3 forbids a menu item for a screen that is not there,
 * so there is still no Promotions entry and no Shipping entry, however obvious
 * the gaps look.
 *
 * Warehouse arrived on 20 Aug 2026 and is deliberately narrow: locations, and
 * where things are. That is phase 5's expensive-to-retrofit half and nothing
 * else — no picking, no waves, no handheld. WarehouseView's header says why
 * that is a decision rather than an unfinished job.
 */
const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/catalog' },
  {
    // Registered with accounts.puida.com exactly — see ACC-001.
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('./views/AuthCallbackView.vue'),
    meta: { public: true },
  },
  {
    path: '/sign-in',
    name: 'sign-in',
    component: () => import('./views/SignInView.vue'),
    meta: { public: true },
  },
  {
    path: '/catalog',
    name: 'catalog',
    component: () => import('./views/CatalogView.vue'),
  },
  {
    // Before /catalog/:sku, or "new" is read as a SKU.
    path: '/catalog/new',
    name: 'product-new',
    component: () => import('./views/NewProductView.vue'),
  },
  {
    path: '/catalog/:sku',
    name: 'product',
    component: () => import('./views/ProductView.vue'),
    props: true,
  },
  {
    // Epic I. A sibling of /catalog rather than a tab inside it: the tree is a
    // different task from the product list, and the two want the whole width.
    path: '/categories',
    name: 'categories',
    component: () => import('./views/CategoriesView.vue'),
  },
  {
    // Epic F. Definitions and schemas are setup rather than daily work, so they
    // get their own screen instead of a panel on the product page.
    path: '/properties',
    name: 'properties',
    component: () => import('./views/PropertiesView.vue'),
  },
  {
    // §4.5. Its own surface rather than a tab under the catalog: a catalog is
    // what the shop sells and an order is something that happened, and the two
    // are read by different people on different days.
    path: '/orders',
    name: 'orders',
    component: () => import('./views/OrdersView.vue'),
  },
  {
    // Its own route, outside the ordinary chrome, because it exists to be
    // printed. Opened in a second tab from an order so the list behind it
    // survives.
    path: '/pick-list',
    name: 'pick-list',
    component: () => import('./views/PickListView.vue'),
    // A document, not a screen: no sidebar, on paper or off it. Still needs a
    // session — see App.vue.
    meta: { bare: true },
  },
  {
    // Like the pick list: its own bare route because it exists to be printed.
    // Keyed by order rather than by query string — one parcel, one slip.
    path: '/packing-slip/:orderNo',
    name: 'packing-slip',
    component: () => import('./views/PackingSlipView.vue'),
    props: true,
    meta: { bare: true },
  },
  {
    path: '/orders/:orderNo',
    name: 'order',
    component: () => import('./views/OrderView.vue'),
    props: true,
  },
  {
    path: '/customers',
    name: 'customers',
    component: () => import('./views/CustomersView.vue'),
  },
  {
    path: '/customers/:id',
    name: 'customer',
    component: () => import('./views/CustomerView.vue'),
    props: true,
  },
  {
    // §4.10 is pre-moderation, so this queue is the only path a review has to
    // being visible at all. It is a screen, not a panel on a product, because
    // the work is "clear the queue" rather than "look after this product".
    path: '/reviews',
    name: 'reviews',
    component: () => import('./views/ReviewsView.vue'),
  },
  {
    // §8.4 — explaining a bank deposit. NOT under /invoices, and not an
    // accident: a settlement is money that already moved, not a document we
    // issued, and `invoicing` is forbidden from reaching a payment provider.
    path: '/settlements',
    name: 'settlements',
    component: () => import('./views/SettlementsView.vue'),
  },
  {
    // §5.7 — "who owes us money" is not a question you ask one order at a
    // time, and until this screen the only way to reach an invoice was to
    // already know which order it came from.
    path: '/invoices',
    name: 'invoices',
    component: () => import('./views/InvoicesView.vue'),
  },
  {
    path: '/invoices/:id',
    name: 'invoice',
    component: () => import('./views/InvoiceView.vue'),
    props: true,
  },
  {
    // §6.4's number, which needs a channel — so it is a screen with a picker
    // rather than a column in the product list that would have to choose one
    // silently. See the note at the top of the view.
    // §13.2's warehouse, or the first honest piece of it. Its own screen and
    // not a tab on Availability: that one answers "what can this shop sell",
    // which is a number, and this one answers "where is it", which is a place.
    path: '/warehouse',
    name: 'warehouse',
    component: () => import('./views/WarehouseView.vue'),
  },
  {
    path: '/availability',
    name: 'availability',
    component: () => import('./views/AvailabilityView.vue'),
  },
  {
    // §4.7's campaigns. A screen rather than a panel on a product for the same
    // reason the moderation queue is one: the work is "what are we running
    // this month", not "look after this product".
    path: '/campaigns',
    name: 'campaigns',
    component: () => import('./views/PromotionsView.vue'),
  },
  {
    // §13.2's fourth surface. API keys first, because they are what lets
    // anything other than a browser reach the API at all.
    path: '/settings',
    name: 'settings',
    component: () => import('./views/SettingsView.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('./views/NotFoundView.vue'),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

/**
 * Everything is authenticated unless it says otherwise.  ACC-002
 *
 * The same shape as the API's guard (§5.1): protection is the default and
 * `meta.public` is the explicit opt-out, so forgetting it makes a screen
 * unreachable rather than open. This is convenience, not security — the API
 * verifies every token regardless, and a client-side guard protects nothing.
 */
let sessionReady: Promise<void> | null = null;

router.beforeEach(async (to) => {
  if (usingDevAuth) return true;

  sessionReady ??= initSession();
  await sessionReady;

  if (to.meta.public) return true;
  if (isSignedIn.value) return true;

  // Remember where they were headed, so a deep link survives sign-in.
  return { name: 'sign-in', query: to.fullPath === '/' ? {} : { next: to.fullPath } };
});
